import {createPaginationContainer, graphql} from 'react-relay';

const ResourceNumbersBreakdownLoader = ({relay, viewer, person, children, ...props}) => {
	const formatData = () => {
		const breakdowns = viewer.company.person.resourceNumbersBreakdown.edges.filter(row => !row.node.project || !row.node.project.projectGroupId);
		const nestedBreakdowns = viewer.company.person.resourceNumbersBreakdown.edges.filter(row => row.node.project?.projectGroupId);

		const resourceNumbersBreakdown = breakdowns
			.map(edge => edge.node)
			.map(breakdown => {
				const {id, resourceNumbers, project, projectGroup, idleTime} = breakdown;
				return {
					id: atob(id),
					resourceNumbers,
					project,
					projectGroup,
					idleTime,
				};
			});
		const projectGroupMap = new Map(viewer.projectGroups.edges.map(projectGroup => [projectGroup.node.id, projectGroup.node]));

		nestedBreakdowns.forEach(breakdown => {
			const {id, resourceNumbers, project, projectGroup, idleTime} = breakdown.node;
			const rowData = {
				id: atob(id),
				resourceNumbers,
				project,
				projectGroup,
				idleTime,
			};

			const projectGroupId = project.projectGroupId;
			let projectGroupRow = resourceNumbersBreakdown.find(row => row.projectGroup?.id === projectGroupId);
			if (!projectGroupRow) {
				projectGroupRow = {
					id: `PROJECT_GROUP-${projectGroupId}`,
					idleTime: null,
					project: null,
					resourceNumbers: {
						actualBillableProjectMinutes: 0,
						actualInternalMinutes: 0,
						actualNonBillableProjectMinutes: 0,
						actualTotalMinutes: 0,
						billableActualVsPlan: 0,
						forecastBillableProjectMinutes: 0,
						forecastInternalMinutes: 0,
						forecastNonBillableProjectMinutes: 0,
						forecastTotalMinutes: 0,
						internalActualVsPlan: 0,
						nonBillableActualVsPlan: 0,
						plannedBillableProjectMinutes: 0,
						plannedInternalMinutes: 0,
						plannedNonBillableProjectMinutes: 0,
						plannedTotalMinutes: 0,
						remainingBillableProjectMinutes: 0,
						remainingInternalMinutes: 0,
						remainingNonBillableProjectMinutes: 0,
						remainingTotalMinutes: 0,
						timeOffMinutes: 0,
						totalActualVsPlan: 0,
					},
					projectGroup: projectGroupMap.get(projectGroupId),
					rows: [rowData],
				};
				resourceNumbersBreakdown.push(projectGroupRow);
			} else {
				if (projectGroupRow.rows) {
					projectGroupRow.rows.push(rowData);
				} else {
					projectGroupRow.rows = [rowData];
				}
			}
		});

		return resourceNumbersBreakdown;
	};

	return children({data: {rows: formatData(), totalCount: viewer.company.person.resourceNumbersBreakdown.totalCount, loadMoreFunc: () => relay.hasMore() && !relay.isLoading() && relay.loadMore(10)}, ...props});
};

export const resourceNumbersBreakdownLoaderQuery = graphql`
	query ResourceNumbersBreakdownLoaderQuery_Query($startYear: Int!, $startMonth: Int!, $startDay: Int!, $endYear: Int!, $endMonth: Int!, $endDay: Int!, $personId: ID, $count: Int, $cursor: String) {
		viewer {
			actualPersonId
			component(name: "ResourceNumbersBreakdownLoader")
			...ResourceNumbersBreakdownLoader_viewer @arguments(startYear: $startYear, startMonth: $startMonth, startDay: $startDay, endYear: $endYear, endMonth: $endMonth, endDay: $endDay, count: $count, cursor: $cursor, personId: $personId)
		}
	}
`;

export default createPaginationContainer(
	ResourceNumbersBreakdownLoader,
	{
		viewer: graphql`
			fragment ResourceNumbersBreakdownLoader_viewer on Viewer
			@argumentDefinitions(
				startYear: {type: "Int!"}
				startMonth: {type: "Int!"}
				startDay: {type: "Int!"}
				endYear: {type: "Int!"}
				endMonth: {type: "Int!"}
				endDay: {type: "Int!"}
				pageSize: {type: "Int"}
				count: {type: "Int"}
				cursor: {type: "String"}
				personId: {type: "ID"}
			)
			@relay(mask: false) {
				company {
					person(id: $personId) {
						id
						resourceNumbersBreakdown(startYear: $startYear, startMonth: $startMonth, startDay: $startDay, endYear: $endYear, endMonth: $endMonth, endDay: $endDay, first: $count, after: $cursor)
							@connection(key: "ResourceNumbersBreakdown_resourceNumbersBreakdown") {
							totalCount
							edges {
								node {
									id
									idleTime {
										id
										name
										isInternalTime
									}
									project {
										id
										companyProjectId
										name
										projectColor
										projectGroupId
										fullAccessToProject
										projectOwner {
											id
											fullName
										}
									}
									projectGroup {
										id
										companyProjectGroupId
										name
										color
									}
									resourceNumbers {
										# Actuals
										actualBillableProjectMinutes
										actualNonBillableProjectMinutes
										actualInternalMinutes
										actualTotalMinutes

										# Plan
										plannedBillableProjectMinutes
										plannedNonBillableProjectMinutes
										plannedInternalMinutes
										plannedTotalMinutes

										# Remaining
										remainingBillableProjectMinutes
										remainingNonBillableProjectMinutes
										remainingInternalMinutes
										remainingTotalMinutes

										# Forecast
										forecastBillableProjectMinutes
										forecastNonBillableProjectMinutes
										forecastInternalMinutes
										forecastTotalMinutes

										# Actuals vs. Plan
										billableActualVsPlan
										nonBillableActualVsPlan
										internalActualVsPlan
										totalActualVsPlan

										# Availability
										timeOffMinutes
									}
								}
							}
						}
					}
				}
				projectGroups(first: 100000) {
					edges {
						node {
							id
							companyProjectGroupId
							name
							color
						}
					}
				}
			}
		`,
	},
	{
		direction: 'forward',
		getConnectionFromProps({viewer}) {
			return viewer.company.person && viewer.company.person.resourceNumbersBreakdown;
		},
		getFragmentVariables(prevVars, totalCount) {
			return {
				...prevVars,
				count: totalCount,
			};
		},
		getVariables(_, {count, cursor}, fragmentVariables) {
			return {
				count,
				cursor,
				personId: fragmentVariables.personId,
			};
		},
		query: graphql`
			query ResourceNumbersBreakdownLoaderQuery($startYear: Int!, $startMonth: Int!, $startDay: Int!, $endYear: Int!, $endMonth: Int!, $endDay: Int!, $count: Int!, $cursor: String, $personId: ID!) {
				viewer {
					actualPersonId
					component(name: "ResourceNumbersBreakdownLoader")
					...ResourceNumbersBreakdownLoader_viewer @arguments(startYear: $startYear, startMonth: $startMonth, startDay: $startDay, endYear: $endYear, endMonth: $endMonth, endDay: $endDay, count: $count, cursor: $cursor, personId: $personId)
				}
			}
		`,
	}
);
